import { type getContainingBrowse } from "#skyward_browseList";
import { mobileAccess } from "#skyward_global";
import { showDialog } from "#skyward_modalLayer";
import { encodeHtml } from "./skyward.base.extensions";
import { registerEvents } from "./skyward.ui.events";
import { JQueryInput } from "./types";

export interface ContentMessageAreaInstance
{
    getContentMessageAreaElement(): JQuery<HTMLElement>;
    resetValidation(): ContentMessageAreaInstance;
    resetConfirmation(): ContentMessageAreaInstance;
    resetInformation(): ContentMessageAreaInstance;
    hideScope(animate?: boolean): void;
    hideInformation(animate?: boolean): void;
    setScopeMessage(message: string): void;
    setInformationMessage(message: string): void;
    setConfirmationMessage(message: string): void;
    setValidationCount(errorCount: number, warningCount: number): ContentMessageAreaInstance;
    getValidationCount(): { errorCount: number; warningCount: number; };
    modifyValidationCount(errorChange: number, warningChange: number): { errorCount: number; warningCount: number; };
    setConfirmButtonName(confirmButtonName: string): ContentMessageAreaInstance;
    setAllErrorsAreHidden(allErrorsAreHidden: boolean): ContentMessageAreaInstance;
    showValidation(): ContentMessageAreaInstance;
    showConfirmation(): ContentMessageAreaInstance;
    showScope(): ContentMessageAreaInstance;
    showInformation(): ContentMessageAreaInstance;
}

interface BaseMessage
{
    message?: JQuery<HTMLElement>;
    show(): BaseMessage;
    hide(animate?: boolean): BaseMessage;
    reset(): BaseMessage;
    isVisible(): boolean | undefined;
}

interface ErrorWarningBaseMessage extends BaseMessage
{
    countSpan?: JQuery<HTMLElement>;
    confirmButtonNameSpan?: JQuery<HTMLElement>;
    setCount?: (newCount: number) => ErrorWarningBaseMessage;
    getCount?: () => number;
    setConfirmButtonName?: (newConfirmButtonName: string) => ErrorWarningBaseMessage;
    getConfirmButtonName?: () => string;
}

interface ErrorMessage extends ErrorWarningBaseMessage
{
    allErrorsAreHidden?: boolean;
}

interface GenericMessage extends BaseMessage
{
    setMessage?: (message: string) => GenericMessage;
}

interface ConfirmationMessage extends BaseMessage
{
    hasValue?: boolean;
    isStandalone?: boolean;
    setMessage?: (message: string) => ConfirmationMessage;
}

const events =
{
    init: initMessagesOnLoad,
    showDetails
};

registerEvents("contentMessageArea", events);

const contentMessageAreasByElement = new WeakMap<HTMLElement, ConfirmationMessage>();
const contentMessageAreasByParent = new WeakMap<HTMLElement, ContentMessageAreaInstance>();
const DEFAULT_CONFIRMATION_MESSAGE = "The information has been successfully saved.";

export function getDefaultConfirmationMessage()
{
    return DEFAULT_CONFIRMATION_MESSAGE;
}

export function getContentMessageArea(container: JQuery<HTMLElement>)
{
    const contentMessageArea = contentMessageAreasByParent.get(container[0]);
    if (contentMessageArea)
    {
        return contentMessageArea;
    }
    // fallback in case the content message area hasn't been initialized yet
    return addContentMessageArea(container.children(".contentMessageArea").first());
}

function initMessagesOnLoad(this: JQueryInput)
{
    const elem = $(this);
    const cmaObj = addContentMessageArea(elem);
    // @ts-ignore Needed to check for script availability in case browse.ts is not included in the bundle (skyward.reportPrint.bundle.js)
    const browse = (skyward.browse?.getContainingBrowse as typeof getContainingBrowse)?.(elem);

    if (browse && !browse.contentMessageArea)
    {
        browse.contentMessageArea = cmaObj;
    }

    elem.parent().find(".toolbar a.button:not(.cancel)").off("click.cma").on("click.cma", function ()
    {
        cmaObj?.getContentMessageAreaElement().attr("data-last-button-clicked-name", $(this).find("span.anchorText").text());
    });
}

function getBaseMessage()
{
    const baseMessage: BaseMessage =
    {
        message: undefined,
        show: showMessage,
        hide: hideMessage,
        reset: resetMessage,
        isVisible: isMessageVisible
    };

    return baseMessage;

    function showMessage()
    {
        if (baseMessage.message)
        {
            baseMessage.message.removeClass("cmaHidden");
        }
        return baseMessage;
    }

    function hideMessage(animate?: boolean)
    {
        if (baseMessage.message)
        {
            if (animate)
            {
                baseMessage.message.animate({ 'opacity': 0 },
                    {
                        complete: function ()
                        {
                            baseMessage.message!.addClass("cmaHidden");
                        }
                    });
            }
            else
            {
                baseMessage.message.addClass("cmaHidden");
            }
        }

        return baseMessage;
    }

    function resetMessage()
    {
        return baseMessage.hide();
    }

    function isMessageVisible()
    {
        return baseMessage.message && !baseMessage.message.hasClass("cmaHidden");
    }
}

function getErrorWarningBaseMessage()
{
    const baseMessage: ErrorWarningBaseMessage = getBaseMessage();

    baseMessage.countSpan = undefined;
    baseMessage.confirmButtonNameSpan = undefined;

    let count = 0;
    let confirmButtonName = "Save";

    baseMessage.setCount = setCount;
    baseMessage.getCount = getCount;
    baseMessage.setConfirmButtonName = setConfirmButtonName;
    baseMessage.getConfirmButtonName = getConfirmButtonName;
    baseMessage.reset = reset;

    return baseMessage;

    function setCount(newCount: number)
    {
        if (baseMessage.countSpan)
        {
            baseMessage.countSpan.text(newCount);
            count = newCount;

            baseMessage.message?.find(".subjectVerbAgreementSpan").each(function ()
            {
                const subjectVerbAgreementSpan = $(this);
                const subjectVerbAgreementSpanData = subjectVerbAgreementSpan.data();

                if (newCount > 1)
                {
                    subjectVerbAgreementSpan.text(subjectVerbAgreementSpanData.plural);
                }
                else
                {
                    subjectVerbAgreementSpan.text(subjectVerbAgreementSpanData.singular);
                }
            });
        }

        return baseMessage;
    }

    function getCount()
    {
        return count;
    }

    function setConfirmButtonName(newConfirmButtonName: string)
    {
        if (baseMessage.confirmButtonNameSpan)
        {
            baseMessage.confirmButtonNameSpan.text(newConfirmButtonName);
            confirmButtonName = newConfirmButtonName;
        }

        return baseMessage;
    }

    function getConfirmButtonName()
    {
        return confirmButtonName;
    }

    function reset()
    {
        return (baseMessage.hide() as ErrorWarningBaseMessage).setCount!(0).setConfirmButtonName!("Save");
    }
}

function getErrorMessage(contentMessageArea: JQuery<HTMLElement>)
{
    const messageObject: ErrorMessage = getErrorWarningBaseMessage();

    const errorMessage = contentMessageArea.find(".cmaErrorMessage");
    const baseReset = messageObject.reset.bind(messageObject);

    if (errorMessage.length === 0)
    {
        return messageObject;
    }

    messageObject.message = errorMessage;
    messageObject.countSpan = messageObject.message.find(".countSpan");
    messageObject.confirmButtonNameSpan = messageObject.message.find(".confirmButtonNameSpan");
    messageObject.allErrorsAreHidden = false;
    messageObject.reset = reset;

    return messageObject;

    function reset()
    {
        messageObject.allErrorsAreHidden = false;
        return baseReset();
    }
}

function getWarningMessage(contentMessageArea: JQuery<HTMLElement>)
{
    const messageObject: ErrorWarningBaseMessage = getErrorWarningBaseMessage();
    const warningMessage = contentMessageArea.find(".cmaWarningMessage");

    if (warningMessage.length === 0)
    {
        return messageObject;
    }

    messageObject.message = warningMessage;
    messageObject.countSpan = messageObject.message.find(".countSpan");
    messageObject.confirmButtonNameSpan = messageObject.message.find(".confirmButtonNameSpan");

    return messageObject;
}

function getConfirmationMessage(contentMessageArea: JQuery<HTMLElement>, contentMessageAreaContainer: JQuery<HTMLElement>)
{
    const messageObject: ConfirmationMessage = getBaseMessage();

    messageObject.hasValue = false;
    messageObject.isStandalone = false;

    let confirmationMessage = contentMessageArea.find(".cmaConfirmationMessage");
    if (confirmationMessage.length === 0)
    {
        messageObject.isStandalone = true;
        confirmationMessage = contentMessageAreaContainer.find(".cmaConfirmationMessage").filter(function ()
        {
            return !contentMessageAreasByElement.has(this);
        }).first();
    }

    if (confirmationMessage.length === 0)
    {
        return messageObject;
    }

    contentMessageAreasByElement.set(confirmationMessage[0], messageObject);
    messageObject.message = confirmationMessage;
    const messageContainer = confirmationMessage.find(".cmaMessage");
    messageObject.hasValue = confirmationMessage.hasClass("cmaHasValue");

    const animateConfirmationMessage = confirmationMessage.hasClass("js-animateConfirmationMessage");

    const base =
    {
        show: messageObject.show.bind(messageObject),
        hide: messageObject.hide.bind(messageObject),
        isVisible: messageObject.isVisible.bind(messageObject)
    };

    messageObject.hide = hide;
    messageObject.show = show;
    messageObject.reset = reset;
    messageObject.isVisible = isVisible;
    messageObject.setMessage = setMessage;

    return messageObject;

    function hide()
    {
        if (!animateConfirmationMessage)
        {
            base.hide();
            toggleSiblings(true);
            return messageObject;
        }

        messageObject.message!.animate({ 'opacity': 0 },
            {
                complete: function ()
                {
                    if (messageObject.isStandalone)
                    {
                        base.hide(false);
                        toggleSiblings(true);
                    }
                    contentMessageArea.trigger("CMAConfirmationHidden");
                }
            });

        return messageObject;
    }

    function show()
    {
        if (animateConfirmationMessage)
        {
            messageObject.message!.attr("aria-hidden", "true");
            messageObject.message!.attr("aria-busy", "true");
        }

        if (messageObject.isStandalone || !animateConfirmationMessage)
        {
            toggleSiblings(false);
            base.show();
        }

        if (!animateConfirmationMessage)
        {
            return messageObject;
        }

        messageObject.message!.animate({ 'opacity': 1 },
            {
                complete: function ()
                {
                    messageObject.message!.attr("aria-busy", "false");
                    messageObject.message!.attr("aria-hidden", "false");
                    window.setTimeout(function ()
                    {
                        messageObject.hide();
                        contentMessageArea.trigger("SaveAnimationRedirect");
                    }, 3000);
                }
            });

        return messageObject;
    }

    function isVisible()
    {
        return !messageObject.isStandalone && base.isVisible();
    }

    function reset()
    {
        messageObject.hide();
        messageContainer.html("");
        messageObject.message?.removeClass("cmaHasValue");
        messageObject.hasValue = false;
        return messageObject;
    }

    // There's a bug in IE 11 where absolutely positioned items in flex containers are incorrectly
    // treated like flex items. To get around this we'll have to instead swap showing the
    // toolbar left and right with the confirmation message.
    // https://developer.microsoft.com/en-us/microsoft-edge/platform/issues/12500543/
    function toggleSiblings(show: boolean)
    {
        if (mobileAccess)
        {
            return;
        }

        messageObject.message!.siblings().toggle(show);
    }

    function setMessage(message: string)
    {
        messageContainer.html(message);
        messageObject.message?.addClass("cmaHasValue");
        messageObject.hasValue = true;
        return messageObject;
    }
}

function getGenericMessage(contentMessageArea: JQuery<HTMLElement>, className: string): GenericMessage
{
    const messageObject: GenericMessage = getBaseMessage();

    const information = contentMessageArea.find(className);
    if (information.length === 0)
    {
        return messageObject;
    }

    messageObject.message = information;
    const messageContainer = information.find(".cmaMessage");
    messageObject.setMessage = setMessage;
    messageObject.reset = reset;

    return messageObject;

    function setMessage(message: string)
    {
        messageContainer.html(message);
        return messageObject;
    }

    function reset()
    {
        messageObject.hide();
        messageContainer.html('');
        return messageObject;
    }
}

function showDetails(event: JQuery.ClickEvent)
{
    const messageContainer = $(event.currentTarget);

    if (messageContainer.closest("a,button").length !== 0)
    {
        return;
    }

    const message = messageContainer!.find(".cmaMessage");
    const messageText = message.find(".cmaMessageText");
    const popupText = messageText.length === 0 ? message.text() : messageText.text();

    showDialog("Additional Information", encodeHtml(popupText), [],
        {
            anchorTo: messageContainer.find(".svg")
        });
}

function getContentMessageAreaObject(contentMessageAreaElement: JQuery<HTMLElement>, contentMessageAreaContainer: JQuery<HTMLElement>)
{
    const contentMessageArea = contentMessageAreaElement;
    const errorMessage = getErrorMessage(contentMessageArea);
    const warningMessage = getWarningMessage(contentMessageArea);
    const confirmationMessage = getConfirmationMessage(contentMessageArea, contentMessageAreaContainer);
    const scopeMessage = getGenericMessage(contentMessageArea, ".cmaScopeMessage");
    const informationMessage = getGenericMessage(contentMessageArea, ".cmaInformationMessage");

    updateVisibility();

    const contentMessageAreaObject: ContentMessageAreaInstance =
    {
        getContentMessageAreaElement,
        resetValidation,
        resetConfirmation,
        resetInformation,
        hideScope,
        hideInformation,
        setScopeMessage,
        setInformationMessage,
        setConfirmationMessage,
        setValidationCount,
        getValidationCount,
        modifyValidationCount,
        setConfirmButtonName,
        setAllErrorsAreHidden,
        showValidation,
        showConfirmation,
        showScope,
        showInformation
    };

    return contentMessageAreaObject;

    function getContentMessageAreaElement()
    {
        return contentMessageArea;
    }

    function updateVisibility()
    {
        const cmaHidden = contentMessageArea.hasClass("cmaHidden");
        const cmaVisible = contentMessageArea.is(":visible");
        const messagesVisible = !!(errorMessage.isVisible() || warningMessage.isVisible() || confirmationMessage.isVisible() || scopeMessage.isVisible() || informationMessage.isVisible());
        if (!cmaHidden && cmaVisible !== messagesVisible)
        {
            contentMessageArea.toggle(messagesVisible);
            /* Some layouts may be affected by showing/hiding the contentMessageArea.
             * Trigger a window resize to force redrawing affected areas.*/
            $(window).trigger("resize");
        }
    }

    function resetValidation()
    {
        errorMessage.reset();
        warningMessage.reset();
        updateVisibility();
        return contentMessageAreaObject;
    }

    function resetConfirmation()
    {
        confirmationMessage.reset();
        updateVisibility();
        return contentMessageAreaObject;
    }

    function resetInformation()
    {
        informationMessage.reset();
        updateVisibility();
        return contentMessageAreaObject;
    }

    function hideScope(animate?: boolean)
    {
        scopeMessage.hide(animate);
        updateVisibility();
    }

    function hideInformation(animate?: boolean)
    {
        informationMessage.hide(animate);
        updateVisibility();
    }

    function setScopeMessage(message: string)
    {
        scopeMessage.setMessage!(message);
        updateVisibility();
    }

    function setInformationMessage(message: string)
    {
        informationMessage.setMessage!(message);
        updateVisibility();
    }

    function setConfirmationMessage(message: string)
    {
        if (confirmationMessage.setMessage)
        {
            confirmationMessage.setMessage(message);
        }
        updateVisibility();
    }

    function setValidationCount(errorCount: number, warningCount: number)
    {
        errorMessage.setCount!(errorCount);
        warningMessage.setCount!(warningCount);
        return contentMessageAreaObject;
    }

    function getValidationCount()
    {
        const count =
        {
            errorCount: errorMessage.getCount!(),
            warningCount: warningMessage.getCount!()
        };
        return count;
    }

    function modifyValidationCount(this: ContentMessageAreaInstance, errorChangeArg: number, warningChangeArg: number)
    {
        const errorChange = isFinite(errorChangeArg) ? errorChangeArg : 0;
        const warningChange = isFinite(warningChangeArg) ? warningChangeArg : 0;
        const { errorCount, warningCount } = this.getValidationCount();
        if (errorCount + errorChange < 0)
        {
            console.warn(`Reducing error count by ${errorChange} but there are only ${errorCount} errors`);
        }
        if (warningCount + warningChange < 0)
        {
            console.warn(`Reducing warning count by ${warningChange} but there are only ${warningCount} warnings`);
        }
        const newErrorCount = Math.max(errorCount + errorChange, 0);
        const newWarningCount = Math.max(warningCount + warningChange, 0);
        this.setValidationCount(newErrorCount, newWarningCount);
        // don't show validation if error count is newly > 0, the confirm button name may not have been initialized yet
        if (newErrorCount === 0 && newWarningCount === 0)
        {
            this.resetValidation();
        }
        return { errorCount: newErrorCount, warningCount: newWarningCount };
    }

    function setConfirmButtonName(confirmButtonName: string)
    {
        errorMessage.setConfirmButtonName!(confirmButtonName);
        warningMessage.setConfirmButtonName!(confirmButtonName);
        return contentMessageAreaObject;
    }

    function setAllErrorsAreHidden(allErrorsAreHidden: boolean)
    {
        errorMessage.allErrorsAreHidden = allErrorsAreHidden;
        return contentMessageAreaObject;
    }

    function showValidation()
    {
        if ((errorMessage.getCount!() === 0 && errorMessage.allErrorsAreHidden === true) || errorMessage.getCount!() > 0)
        {
            if (errorMessage.allErrorsAreHidden === true)
            {
                errorMessage.message!.find(".cmaMessageText").text("Errors were encountered while attempting to Save. Please contact your Skyward Administrator.");
            }
            errorMessage.setConfirmButtonName!(contentMessageArea.attr("data-last-button-clicked-name")!);
            errorMessage.show();
        }

        if (warningMessage.getCount!() > 0)
        {
            warningMessage.setConfirmButtonName!(contentMessageArea.attr("data-last-button-clicked-name")!);
            warningMessage.show();
        }

        updateVisibility();

        return contentMessageAreaObject;
    }

    function showConfirmation()
    {
        if (confirmationMessage.hasValue)
        {
            confirmationMessage.show();
        }

        updateVisibility();
        return contentMessageAreaObject;
    }

    function showScope()
    {
        scopeMessage.show();
        updateVisibility();
        return contentMessageAreaObject;
    }

    function showInformation()
    {
        informationMessage.show();
        updateVisibility();
        return contentMessageAreaObject;
    }
}

function addContentMessageArea(element: JQuery<HTMLElement>)
{
    if (element.length === 0)
    {
        return null;
    }

    const container = element.parent();
    contentMessageAreasByParent.delete(container[0]);

    let contentMessageAreaElement;
    if (mobileAccess && container.hasClass("js-mobileToolbarControlledContent"))
    {
        contentMessageAreaElement = container.siblings(".js-mobileDetailsCMA").first();
    }
    else
    {
        contentMessageAreaElement = element;
    }

    if (contentMessageAreaElement.length > 0)
    {
        const cmaObject = getContentMessageAreaObject(contentMessageAreaElement, container);
        contentMessageAreasByParent.set(container[0], cmaObject);
        cmaObject.showConfirmation();
        return cmaObject;
    }

    return null;
}
